import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { UtilDialogService } from '../../services/dialog.service';

export interface SupplierUnavailableDialogData {
  message: string;
  accountUserId: string;
  supplierId: string;
  /** The reason emailed to the supplier administrator. Distinct from `message` so the user-facing
   *  warning text and the operator-facing alert text can differ if needed. */
  emailReason: string;
  dialogService: UtilDialogService;
}

@Component({
  selector: 'prox-supplier-unavailable-dialog',
  template: `
    <div class="supplier-unavailable-container">
      <div class="warning-card">
        <i class="pi pi-exclamation-triangle warning-icon"></i>
        <div class="warning-message">{{ data.message }}</div>
      </div>

      <div *ngIf="!sending && !sent && !sendError" class="contact-footer">
        Click <strong>Contact Administrator</strong> to alert the administrator who manages this supplier for your account.
      </div>

      <div *ngIf="sent" class="email-confirmation">
        <i class="pi pi-check-circle"></i>
        {{ confirmationMessage }}
      </div>

      <div *ngIf="sendError" class="email-error">
        <i class="pi pi-times-circle"></i>
        {{ sendError }}
      </div>

      <div class="button-row">
        <button
          (click)="close()"
          pButton
          type="button"
          icon="pi pi-times"
          label="Close"
          class="p-button-raised"></button>
        <button
          *ngIf="!sent"
          (click)="contactAdmin()"
          pButton
          type="button"
          icon="pi pi-envelope"
          label="Contact Administrator"
          class="p-button-raised p-button-secondary"
          [disabled]="sending"
          [loading]="sending"></button>
      </div>
    </div>
  `,
  styles: [
    `
      .supplier-unavailable-container {
        font-size: 14px;
        line-height: 1.6;
      }

      .warning-card {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        margin-bottom: 16px;
        padding: 12px;
        background-color: #fef3c7;
        border-left: 4px solid #f59e0b;
        border-radius: 4px;
      }

      .warning-icon {
        font-size: 1.4rem;
        color: #b45309;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .warning-message {
        font-weight: 500;
      }

      .contact-footer {
        margin-top: 16px;
        padding: 12px 0;
        text-align: center;
        font-size: 15px;
      }

      .email-confirmation {
        margin-top: 12px;
        padding: 12px;
        background-color: #d1fae5;
        border-left: 4px solid #10b981;
        border-radius: 4px;
        color: #065f46;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .email-confirmation i {
        font-size: 1.2rem;
      }

      .email-error {
        margin-top: 12px;
        padding: 12px;
        background-color: #fee2e2;
        border-left: 4px solid #ef4444;
        border-radius: 4px;
        color: #991b1b;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .email-error i {
        font-size: 1.2rem;
      }

      .button-row {
        margin-top: 16px;
        display: flex;
        gap: 8px;
        justify-content: flex-end;
      }
    `,
  ],
  standalone: false,
})
export class SupplierUnavailableDialogComponent {
  data: SupplierUnavailableDialogData;
  sending = false;
  sent = false;
  sendError: string | null = null;
  confirmationMessage = '';

  constructor(dialogConfig: DynamicDialogConfig, private ref: DynamicDialogRef) {
    this.data = dialogConfig.data;
  }

  close() {
    this.ref.close();
  }

  async contactAdmin() {
    this.sending = true;
    this.sendError = null;
    try {
      const response = await this.data.dialogService.contactSupplierAdminAboutBlockedItem(
        this.data.accountUserId,
        this.data.supplierId,
        this.data.emailReason,
      );
      if (response.success && response.emailsSent > 0) {
        this.sent = true;
        this.confirmationMessage = response.message || 'Administrator has been notified.';
      } else {
        this.sendError = response.message || 'Unable to notify the administrator.';
      }
    } catch (e: any) {
      this.sendError = e?.error?.message || e?.message || 'Unable to notify the administrator.';
    } finally {
      this.sending = false;
    }
  }
}
